import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

interface ImportResult {
  success: boolean;
  imported: number;
  failed: number;
  errors: Array<{ cardNumber: string; error: string }>;
  cards: any[];
}

interface User {
  role?: string;
}

const AdminCSVImportPage = () => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = () => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');

    if (!token) {
      setMessage({ type: 'error', text: 'Please login to access this page' });
      setTimeout(() => navigate('/login'), 2000);
      return;
    }

    if (userStr) {
      try {
        const user: User = JSON.parse(userStr);
        if (user.role !== 'admin') {
          setMessage({ type: 'error', text: 'Admin access required' });
          setTimeout(() => navigate('/dashboard'), 2000);
          return;
        }
      } catch (error) {
        console.error('Failed to parse user:', error);
        navigate('/login');
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.name.toLowerCase().endsWith('.csv')) {
        setMessage({ type: 'error', text: 'Please select a valid CSV file' });
        return;
      }

      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'File size must be less than 10MB' });
        return;
      }

      setSelectedFile(file);
      setMessage(null);
      setImportResult(null);
    }
  };

  const handleUploadCSV = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedFile) {
      setMessage({ type: 'error', text: 'Please select a CSV file' });
      return;
    }

    try {
      setImporting(true);
      setMessage(null);
      setImportResult(null);

      const formData = new FormData();
      formData.append('csv', selectedFile);

      const token = localStorage.getItem('token');
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

      console.log('Uploading CSV to:', `${API_URL}/cards/import/csv`);

      const response = await axios.post(
        `${API_URL}/cards/import/csv`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const result: ImportResult = response.data.data;
      setImportResult(result);

      // Show success if no cards failed (even if result.success is false)
      if (result.failed === 0) {
        setMessage({
          type: 'success',
          text: `Successfully imported ${result.imported} cards!`
        });
      } else {
        setMessage({
          type: 'error',
          text: `Import completed with errors: ${result.imported} imported, ${result.failed} failed`
        });
      }

      // Reset file input
      setSelectedFile(null);
      const fileInput = document.getElementById('file-input') as HTMLInputElement;
      if (fileInput) fileInput.value = '';

    } catch (error: any) {
      console.error('Import failed:', error);

      let errorMessage = 'Failed to import CSV. ';

      if (error.response) {
        errorMessage += error.response.data?.message || `Server error: ${error.response.status}`;
        if (error.response.status === 401) {
          errorMessage += ' Please login again.';
          setTimeout(() => navigate('/login'), 2000);
        } else if (error.response.status === 403) {
          errorMessage += ' Admin access required.';
          setTimeout(() => navigate('/dashboard'), 2000);
        }
      } else if (error.request) {
        errorMessage += 'Cannot connect to server. Please ensure the backend is running.';
      } else {
        errorMessage += error.message;
      }

      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setImporting(false);
    }
  };

  const handleImportFromDirectory = async () => {
    try {
      setImporting(true);
      setMessage(null);
      setImportResult(null);

      const token = localStorage.getItem('token');
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

      console.log('Importing from directory:', `${API_URL}/cards/import/directory`);

      const response = await axios.post(
        `${API_URL}/cards/import/directory`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result: ImportResult = response.data.data;
      setImportResult(result);

      // Show success if no cards failed (even if result.success is false)
      if (result.failed === 0) {
        setMessage({
          type: 'success',
          text: `Successfully imported ${result.imported} cards from card_sources directory!`
        });
      } else {
        setMessage({
          type: 'error',
          text: `Import completed with errors: ${result.imported} imported, ${result.failed} failed`
        });
      }

    } catch (error: any) {
      console.error('Directory import failed:', error);

      let errorMessage = 'Failed to import from directory. ';

      if (error.response) {
        errorMessage += error.response.data?.message || `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage += 'Cannot connect to server.';
      } else {
        errorMessage += error.message;
      }

      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">Admin - Import Cards from CSV</h1>
        <p className="text-gray-600">
          Bulk import trading cards from CSV files
        </p>
      </div>

      {/* Message Alert */}
      {message && (
        <div
          className={`mb-6 p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : message.type === 'error'
              ? 'bg-red-50 border border-red-200 text-red-700'
              : 'bg-blue-50 border border-blue-200 text-blue-700'
          }`}
        >
          <div className="flex items-center">
            <span className="text-xl mr-2">
              {message.type === 'success' ? '✓' : message.type === 'error' ? '⚠' : 'ℹ️'}
            </span>
            <span>{message.text}</span>
          </div>
        </div>
      )}

      {/* CSV Format Info */}
      <div className="mb-6 p-4 rounded-lg bg-blue-50 border border-blue-200">
        <h3 className="font-bold text-blue-900 mb-2">📋 CSV Format Requirements</h3>
        <p className="text-sm text-blue-800 mb-2">
          Your CSV file should follow this format:
        </p>
        <ul className="text-sm text-blue-800 list-disc list-inside space-y-1">
          <li>First 2 columns should be empty</li>
          <li>Header row: Name, Type, Cost, Gem, Power, Symbol, Color, Ex, ซอย, Print, Rare, Drop Rate</li>
          <li>Card data rows with all required fields</li>
          <li>Main Effect should be on a separate row after each card</li>
        </ul>
        <p className="text-sm text-blue-800 mt-2">
          <strong>Example:</strong> See <code className="bg-blue-100 px-1 rounded">card_sources/sample_cards.csv</code>
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Left Column - Upload CSV File */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-6">Upload CSV File</h2>

          <form onSubmit={handleUploadCSV} className="space-y-6">
            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CSV File *
              </label>
              <input
                id="file-input"
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
                disabled={importing}
              />
              <p className="text-xs text-gray-500 mt-1">
                Accepted format: CSV (Max 10MB)
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={importing || !selectedFile}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition font-medium disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {importing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Importing...
                </>
              ) : (
                'Upload and Import CSV'
              )}
            </button>
          </form>
        </div>

        {/* Right Column - Import from Directory */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-6">Import from card_sources</h2>

          <div className="space-y-4">
            <p className="text-gray-600">
              Import all CSV files from the <code className="bg-gray-100 px-2 py-1 rounded">card_sources</code> directory on the server.
            </p>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> This will import all CSV files found in the card_sources directory.
                Make sure your CSV files are properly formatted before importing.
              </p>
            </div>

            <button
              onClick={handleImportFromDirectory}
              disabled={importing}
              className="w-full bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 transition font-medium disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {importing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Importing...
                </>
              ) : (
                'Import from Directory'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Import Results */}
      {importResult && (
        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-6">Import Results</h2>

          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-gray-600">{importResult.cards.length}</div>
              <div className="text-sm text-gray-600">Total Cards Found</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-green-600">{importResult.imported}</div>
              <div className="text-sm text-gray-600">Successfully Imported</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-red-600">{importResult.failed}</div>
              <div className="text-sm text-gray-600">Failed</div>
            </div>
          </div>

          {/* Errors */}
          {importResult.errors.length > 0 && (
            <div>
              <h3 className="font-bold text-red-900 mb-2">Errors:</h3>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-64 overflow-y-auto">
                <ul className="text-sm text-red-800 space-y-2">
                  {importResult.errors.map((error, index) => (
                    <li key={index}>
                      <strong>{error.cardNumber}:</strong> {error.error}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Imported Cards Preview */}
          {importResult.cards.length > 0 && (
            <div className="mt-6">
              <h3 className="font-bold text-gray-900 mb-2">Imported Cards:</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-300">
                      <th className="text-left py-2 px-2">Card Number</th>
                      <th className="text-left py-2 px-2">Name</th>
                      <th className="text-left py-2 px-2">Type</th>
                      <th className="text-left py-2 px-2">Rarity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importResult.cards.map((card, index) => (
                      <tr key={index} className="border-b border-gray-200">
                        <td className="py-2 px-2">{card.cardNumber}</td>
                        <td className="py-2 px-2">{card.name}</td>
                        <td className="py-2 px-2">{card.type}</td>
                        <td className="py-2 px-2">{card.rarity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Back Button */}
      <div className="mt-8 text-center">
        <button
          onClick={() => navigate('/dashboard')}
          className="text-blue-600 hover:text-blue-700 font-medium"
        >
          ← Back to Dashboard
        </button>
      </div>
    </div>
  );
};

export default AdminCSVImportPage;
